import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import SEOHelmet from "@/components/base/SEOHelmet";
import Breadcrumb from "@/components/base/Breadcrumb";
import { brandInfo, reputationItems, contactInfo, faqItems } from "@/mocks/about";

const aboutSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "关于91看网页版",
  description: brandInfo.description,
  url: "https://www.91kan.com/about",
  mainEntity: {
    "@type": "Organization",
    name: "91看网页版",
    description: brandInfo.description,
    foundingDate: `${brandInfo.foundedYear}`,
    email: contactInfo.email,
    telephone: contactInfo.phone,
    address: {
      "@type": "PostalAddress",
      streetAddress: "建国路88号SOHO现代城A座18层",
      addressLocality: "北京",
      addressRegion: "朝阳区",
      addressCountry: "CN",
    },
  },
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqItems.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer,
    },
  })),
};

export default function About() {
  return (
    <>
      <ScrollToTop />
      <SEOHelmet
        title="关于我们 - 91看网页版官方正版在线观看平台"
        description="了解91看网页版的品牌故事、用户口碑和联系方式。91看网页版是国内领先的在线影视观看平台，无需下载即可畅享海量高清视频内容。"
        keywords="91看网页版,91看网页版关于,91看网页版入口,91看网页版联系方式,91看官网网页版"
        canonicalUrl="https://www.91kan.com/about"
        schemaData={{ ...aboutSchema, ...faqSchema }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <Breadcrumb
              items={[
                { name: "首页", path: "/" },
                { name: "关于我们" },
              ]}
            />

            {/* Hero */}
            <section className="text-center py-10 md:py-14">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
                关于91看网页版
              </h1>
              <p className="text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                {brandInfo.subtitle}
              </p>
            </section>

            {/* Brand Intro */}
            <section className="mb-14">
              <div className="rounded-2xl bg-background-100 p-8 md:p-10">
                <h2 className="text-xl font-bold text-foreground-950 mb-5" style={{ fontFamily: "var(--font-heading)" }}>
                  品牌介绍
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-8">
                  {brandInfo.description}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="rounded-xl bg-background-50 p-5">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-50 text-accent-500 mb-3">
                      <i className="ri-focus-3-line text-lg"></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">使命</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{brandInfo.mission}</p>
                  </div>
                  <div className="rounded-xl bg-background-50 p-5">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-50 text-accent-500 mb-3">
                      <i className="ri-eye-line text-lg"></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">愿景</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{brandInfo.vision}</p>
                  </div>
                  <div className="rounded-xl bg-background-50 p-5">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-50 text-accent-500 mb-3">
                      <i className="ri-calendar-line text-lg"></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">成立时间</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{brandInfo.foundedYear}年</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Values */}
            <section className="mb-14">
              <h2 className="text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
                核心价值观
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {brandInfo.values.map((value, idx) => (
                  <div key={idx} className="rounded-xl bg-background-100 p-5 text-center">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mx-auto mb-3">
                      <span className="text-sm font-bold">{idx + 1}</span>
                    </div>
                    <span className="text-sm font-medium text-foreground-800">{value}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Reputation */}
            <section className="mb-14">
              <h2 className="text-xl font-bold text-foreground-950 mb-2" style={{ fontFamily: "var(--font-heading)" }}>
                用户口碑
              </h2>
              <p className="text-sm text-foreground-500 mb-6">来自真实用户的使用评价</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {reputationItems.map((item) => (
                  <div key={item.id} className="rounded-xl bg-background-100 p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <img
                        src={item.avatar}
                        alt={item.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <p className="text-sm font-medium text-foreground-900">{item.name}</p>
                        <div className="flex items-center gap-0.5 mt-0.5">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <i
                              key={i}
                              className={`text-[10px] ${i < item.rating ? "ri-star-fill text-yellow-500" : "ri-star-fill text-foreground-200"}`}
                            ></i>
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-foreground-500 leading-relaxed mb-3">
                      {item.content}
                    </p>
                    <span className="text-[11px] text-foreground-300">{item.date}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact */}
            <section className="mb-14">
              <h2 className="text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
                联系我们
              </h2>
              <div className="rounded-2xl bg-background-100 p-8 md:p-10">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-50 text-accent-500 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                      <p className="text-sm text-foreground-500">{contactInfo.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-50 text-accent-500 flex-shrink-0">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">客服电话</h3>
                      <p className="text-sm text-foreground-500">{contactInfo.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-50 text-accent-500 flex-shrink-0">
                      <i className="ri-wechat-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">官方微信</h3>
                      <p className="text-sm text-foreground-500">{contactInfo.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-50 text-accent-500 flex-shrink-0">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">工作时间</h3>
                      <p className="text-sm text-foreground-500">{contactInfo.workingHours}</p>
                    </div>
                  </div>
                  <div className="sm:col-span-2 flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-50 text-accent-500 flex-shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h3>
                      <p className="text-sm text-foreground-500">{contactInfo.address}</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* FAQ */}
            <section className="pb-14">
              <h2 className="text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
                常见问题
              </h2>
              <div className="space-y-3">
                {faqItems.map((faq, idx) => (
                  <details key={idx} className="group rounded-xl bg-background-100 border border-background-200/70">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                      <span className="text-sm font-medium text-foreground-800 pr-4">{faq.question}</span>
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200 text-lg flex-shrink-0"></i>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-500 leading-relaxed">{faq.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </section>

            {/* CTA */}
            <section className="pb-14 text-center">
              <div className="rounded-2xl bg-foreground-950 p-10 md:p-14">
                <h2 className="text-xl md:text-2xl font-bold text-background-50 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
                  还有疑问？
                </h2>
                <p className="text-sm text-background-50/60 mb-6">
                  随时联系我们的客服团队，我们将竭诚为您服务
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center px-6 py-3 rounded-full bg-accent-500 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-accent-600 transition-colors duration-200"
                >
                  <i className="ri-home-line mr-2"></i>
                  返回首页
                </Link>
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}